import { PHYSICS } from '../constants/physics.js';
import type {
  CollisionType,
  PlayerCollisionEvent,
  PlayerState,
} from '../types/index.js';
import { COLLISION_BALANCE, type CollisionBalanceConfig } from './collision-config.js';
import { SpatialHash } from './spatial-hash.js';

interface Vector2 {
  x: number;
  y: number;
}

// La simulación local solo necesita acceso al mapa de jugadores.
export interface SimulationPlayersState {
  players: Map<string, PlayerState>;
}

type CollisionRuntime = {
  pairCooldownUntil: Map<string, number>;
  immunityUntil: Map<string, number>;
  recoveryUntil: Map<string, number>;
  eventSequence: number;
};

export class PhysicsEngine {
  private readonly config: Readonly<CollisionBalanceConfig>;
  private readonly runtime: CollisionRuntime = {
    pairCooldownUntil: new Map(),
    immunityUntil: new Map(),
    recoveryUntil: new Map(),
    eventSequence: 0,
  };

  constructor(config: Readonly<CollisionBalanceConfig> = COLLISION_BALANCE) {
    this.config = config;
  }

  update(state: SimulationPlayersState, spatialHash: SpatialHash, tick: number): PlayerCollisionEvent[] {
    for (const player of state.players.values()) {
      if (!player.alive) continue;
      this.applyDesiredMovement(player, tick);
    }

    spatialHash.clear();
    for (const player of state.players.values()) {
      if (player.alive) spatialHash.insert(player, player.x, player.y, player.radius);
    }

    const events = this.resolveCollisions(state, spatialHash, tick);
    for (const player of state.players.values()) {
      if (!player.alive) continue;
      player.x += player.vx;
      player.y += player.vy;
      const hasMovementInput = Math.hypot(player.desiredDirX, player.desiredDirY) > 1e-4;
      if (!hasMovementInput || player.knockbackTicks > 0) {
        player.vx = this.damp(player.vx);
        player.vy = this.damp(player.vy);
      }
    }
    this.pruneRuntime(tick);
    return events;
  }

  getMovementControlFactor(player: PlayerState, tick: number): number {
    const recoveryUntil = this.runtime.recoveryUntil.get(player.id) ?? 0;
    if (tick >= recoveryUntil) return 1;
    const controlLevel = player.upgrades.control;
    return Math.min(
      0.45,
      this.config.airborneControlFactor + controlLevel * this.config.controlRecoveryFactor,
    );
  }

  private resolveCollisions(
    state: SimulationPlayersState,
    spatialHash: SpatialHash,
    tick: number,
  ): PlayerCollisionEvent[] {
    const checked = new Set<string>();
    const events: PlayerCollisionEvent[] = [];

    for (const a of state.players.values()) {
      if (!a.alive) continue;
      const nearby = spatialHash.query(a.x, a.y, a.radius * 3);
      for (const entity of nearby) {
        const b = state.players.get(entity.id);
        if (!b || !b.alive || a.id === b.id) continue;

        const pairKey = this.pairKey(a.id, b.id);
        if (checked.has(pairKey)) continue;
        checked.add(pairKey);

        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const distanceSquared = dx * dx + dy * dy;
        const minimumDistance = a.radius + b.radius;
        if (distanceSquared >= minimumDistance * minimumDistance) continue;

        const distance = Math.sqrt(distanceSquared);
        const normal = distance > 1e-8
          ? { x: dx / distance, y: dy / distance }
          : this.stableFallbackNormal(a.id, b.id);
        this.separate(a, b, normal, minimumDistance - distance);

        if ((this.runtime.pairCooldownUntil.get(pairKey) ?? 0) > tick) {
          this.cancelClosingVelocity(a, b, normal);
          continue;
        }

        const event = this.applyImpact(a, b, normal, tick);
        if (event) {
          this.runtime.pairCooldownUntil.set(pairKey, tick + this.msToTicks(this.config.collisionCooldownMs));
          events.push(event);
        }
      }
    }
    return events;
  }

  private applyImpact(
    a: PlayerState,
    b: PlayerState,
    normal: Vector2,
    tick: number,
  ): PlayerCollisionEvent | null {
    const approachA = Math.max(0, this.dot(a.vx, a.vy, normal.x, normal.y));
    const approachB = Math.max(0, this.dot(b.vx, b.vy, -normal.x, -normal.y));

    if (
      approachA < this.config.minimumApproachSpeed
      && approachB < this.config.minimumApproachSpeed
    ) return null;

    const effectivenessA = this.angleEffectiveness(a.vx, a.vy, normal.x, normal.y);
    const effectivenessB = this.angleEffectiveness(b.vx, b.vy, -normal.x, -normal.y);
    const impactA = approachA
      * (1 + a.upgrades.push * this.config.pushPowerPerLevel)
      * (1 + a.upgrades.weight * this.config.offensiveWeightFactor)
      * effectivenessA;
    const impactB = approachB
      * (1 + b.upgrades.push * this.config.pushPowerPerLevel)
      * (1 + b.upgrades.weight * this.config.offensiveWeightFactor)
      * effectivenessB;
    const netImpact = impactA - impactB;
    const impulseA = { x: 0, y: 0 };
    const impulseB = { x: 0, y: 0 };
    let collisionType = this.classifyCollision(a, b, effectivenessA, effectivenessB, netImpact);

    if (Math.abs(netImpact) <= this.config.impactDeadZone) {
      collisionType = 'balanced';
      const bounce = this.clamp(
        Math.max(impactA, impactB) * this.config.impulseScale * this.config.balancedBounceFactor,
        this.config.minCollisionImpulse,
        this.config.maxCollisionImpulse,
      );
      this.assignImpulse(impulseA, -normal.x * bounce, -normal.y * bounce);
      this.assignImpulse(impulseB, normal.x * bounce, normal.y * bounce);
    } else if (netImpact > 0) {
      const received = this.impulseMagnitude(netImpact, b.upgrades.weight);
      const recoil = received * this.config.winnerRecoilFactor;
      this.assignImpulse(impulseA, -normal.x * recoil, -normal.y * recoil);
      if (!this.isImmune(b.id, tick)) {
        this.assignImpulse(impulseB, normal.x * received, normal.y * received);
      }
    } else {
      const received = this.impulseMagnitude(-netImpact, a.upgrades.weight);
      const recoil = received * this.config.winnerRecoilFactor;
      if (!this.isImmune(a.id, tick)) {
        this.assignImpulse(impulseA, -normal.x * received, -normal.y * received);
      }
      this.assignImpulse(impulseB, normal.x * recoil, normal.y * recoil);
    }

    this.applyImpulse(a, impulseA, tick);
    this.applyImpulse(b, impulseB, tick);

    return {
      id: `${tick}:${this.runtime.eventSequence++}:${this.pairKey(a.id, b.id)}`,
      serverTick: tick,
      playerAId: a.id,
      playerBId: b.id,
      position: { x: (a.x + b.x) * 0.5, y: (a.y + b.y) * 0.5 },
      normal,
      approachSpeedA: approachA,
      approachSpeedB: approachB,
      impactA,
      impactB,
      netImpact,
      impulseA,
      impulseB,
      collisionType,
    };
  }

  private separate(a: PlayerState, b: PlayerState, normal: Vector2, penetration: number): void {
    const correction = (Math.max(0, penetration) + this.config.separationDistance) * 0.5;
    a.x -= normal.x * correction;
    a.y -= normal.y * correction;
    b.x += normal.x * correction;
    b.y += normal.y * correction;
  }

  private cancelClosingVelocity(a: PlayerState, b: PlayerState, normal: Vector2): void {
    const approachA = Math.max(0, this.dot(a.vx, a.vy, normal.x, normal.y));
    const approachB = Math.max(0, this.dot(b.vx, b.vy, -normal.x, -normal.y));
    a.vx -= normal.x * approachA;
    a.vy -= normal.y * approachA;
    b.vx += normal.x * approachB;
    b.vy += normal.y * approachB;
  }

  private impulseMagnitude(netImpact: number, weightLevel: number): number {
    const resistance = 1 + weightLevel * this.config.defensiveWeightFactor;
    return this.clamp(
      (netImpact / resistance) * this.config.impulseScale,
      this.config.minCollisionImpulse,
      this.config.maxCollisionImpulse,
    );
  }

  private applyImpulse(player: PlayerState, impulse: Vector2, tick: number): void {
    player.vx += impulse.x;
    player.vy += impulse.y;
    const speed = Math.hypot(player.vx, player.vy);
    if (speed > this.config.maxKnockbackSpeed) {
      const scale = this.config.maxKnockbackSpeed / speed;
      player.vx *= scale;
      player.vy *= scale;
    }
    const magnitude = Math.hypot(impulse.x, impulse.y);
    if (magnitude >= this.config.strongImpactThreshold) {
      this.runtime.immunityUntil.set(
        player.id,
        tick + this.msToTicks(this.config.knockbackImmunityMs),
      );
      const controlLevel = player.upgrades.control;
      const recoveryMs = this.config.recoveryDurationMs / (1 + controlLevel * 0.16);
      this.runtime.recoveryUntil.set(player.id, tick + this.msToTicks(recoveryMs));
      player.knockbackTicks = this.msToTicks(recoveryMs);
      player.impactImmunityUntilTick = tick + this.msToTicks(this.config.knockbackImmunityMs);
    }
  }

  private classifyCollision(
    a: PlayerState,
    b: PlayerState,
    effectivenessA: number,
    effectivenessB: number,
    netImpact: number,
  ): CollisionType {
    if (Math.abs(netImpact) <= this.config.impactDeadZone) return 'balanced';
    const speedA = Math.hypot(a.vx, a.vy);
    const speedB = Math.hypot(b.vx, b.vy);
    const directionDot = speedA > 1e-8 && speedB > 1e-8
      ? this.dot(a.vx / speedA, a.vy / speedA, b.vx / speedB, b.vy / speedB)
      : 0;
    if (effectivenessA > 0.65 && effectivenessB > 0.65) return 'frontal';
    if (directionDot > 0.5 && Math.max(effectivenessA, effectivenessB) > 0.45) return 'rear';
    if (
      Math.max(effectivenessA, effectivenessB) > 0.55
      && Math.min(effectivenessA, effectivenessB) < 0.25
    ) return 'lateral';
    return 'glancing';
  }

  private angleEffectiveness(vx: number, vy: number, nx: number, ny: number): number {
    const speed = Math.hypot(vx, vy);
    return speed > 1e-8 ? this.clamp(this.dot(vx / speed, vy / speed, nx, ny), 0, 1) : 0;
  }

  private applyDesiredMovement(player: PlayerState, tick: number): void {
    const desiredLength = Math.hypot(player.desiredDirX, player.desiredDirY);
    const dirX = desiredLength > 1e-8 ? player.desiredDirX / desiredLength : 0;
    const dirY = desiredLength > 1e-8 ? player.desiredDirY / desiredLength : 0;
    const maxSpeed = PHYSICS.BASE_SPEED_PER_TICK
      * (1 + player.upgrades.speed * this.config.speedPerLevel)
      * Math.max(0.55, 1 - player.upgrades.weight * this.config.weightSpeedPenalty);
    const acceleration = this.config.baseAcceleration
      * (1 + player.upgrades.control * this.config.controlAccelerationFactor)
      * Math.max(0.55, 1 - player.upgrades.weight * this.config.weightAccelerationPenalty);
    const control = this.getMovementControlFactor(player, tick);
    const targetVx = dirX * maxSpeed;
    const targetVy = dirY * maxSpeed;
    player.vx += (targetVx - player.vx) * acceleration * control;
    player.vy += (targetVy - player.vy) * acceleration * control;
    player.knockbackTicks = Math.max(0, player.knockbackTicks - 1);
  }

  private isImmune(playerId: string, tick: number): boolean {
    return (this.runtime.immunityUntil.get(playerId) ?? 0) > tick;
  }

  private damp(value: number): number {
    const damped = value * this.config.velocityDamping;
    return Math.abs(damped) < this.config.stopSpeed ? 0 : damped;
  }

  private pruneRuntime(tick: number): void {
    for (const [key, until] of this.runtime.pairCooldownUntil) {
      if (until <= tick) this.runtime.pairCooldownUntil.delete(key);
    }
    for (const map of [this.runtime.immunityUntil, this.runtime.recoveryUntil]) {
      for (const [key, until] of map) if (until <= tick) map.delete(key);
    }
  }

  private msToTicks(ms: number): number {
    return Math.max(1, Math.ceil(ms / PHYSICS.TICK_INTERVAL));
  }

  private pairKey(a: string, b: string): string {
    return a < b ? `${a}:${b}` : `${b}:${a}`;
  }

  private stableFallbackNormal(a: string, b: string): Vector2 {
    return a < b ? { x: 1, y: 0 } : { x: -1, y: 0 };
  }

  private dot(ax: number, ay: number, bx: number, by: number): number {
    return ax * bx + ay * by;
  }

  private assignImpulse(target: Vector2, x: number, y: number): void {
    target.x = x;
    target.y = y;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }
}